/**
 * Living defences and threats around owned houses (docs/DESIGN_PROPERTY.md, slice 4):
 * hired guards posted at the door, and night-time burglars who loot the stash when nobody
 * is home. Both are ordinary `Person`s driven here every step.
 */
import { smokeBetween } from './charges';
import type { Game } from './game';
import { makePerson, type Person } from './entities';
import { doorstep, guardFollows, powered, type Property } from './property';
import { CELL_FLOOR, floorPlan, planPath } from '../world/interior';
import { updatePerson } from './physics';
import { spawnGangAt } from './jobs';
import { karma, KARMA } from './animals';

const GUARD_LEASH = 12;          // tiles a guard strays from the post
const BURGLAR_STAY = 20;         // seconds inside before the loot is bagged
const BURGLAR_CHANCE_PER_HOUR = 0.15;
const NIGHT = (h: number) => h >= 22 || h < 5;

/** Walk `p` towards (gx, gy) with the player physics; returns the remaining distance. */
function walkTo(game: Game, p: Person, gx: number, gy: number, dt: number, sprint = false): number {
  const dx = gx - p.x, dy = gy - p.y, d = Math.hypot(dx, dy);
  if (d < 0.15) return d;
  const x0 = p.x, y0 = p.y;
  updatePerson(game.world, p, dx / d, dy / d, sprint, dt, false, game.vehicles);
  p.angle = Math.atan2(dx, -dy);
  return Math.hypot(p.x - x0, p.y - y0) < 0.01 ? -d : d; // negative: did not move (blocked)
}

/**
 * Walk towards a goal, routing through the floor plan when indoors (BFS waypoints), straight
 * otherwise. Returns the distance left (negative when no progress this step).
 */
function navTo(game: Game, q: Person, gx: number, gy: number, dt: number, sprint = false): number {
  const plan = q.inside ? game.world.planAt(q.x, q.y, q.floor) : undefined;
  if (!plan) { q.nav = undefined; return walkTo(game, q, gx, gy, dt, sprint); }
  let nav = q.nav;
  if (!nav || Math.hypot(nav.goal[0] - gx, nav.goal[1] - gy) > 0.5 || nav.stuck > 2) {
    const path = planPath(plan, q.x, q.y, gx, gy);
    nav = q.nav = { goal: [gx, gy], path: path ?? [[gx, gy]], stuck: 0 };
  }
  while (nav.path.length > 1 && Math.hypot(nav.path[0][0] - q.x, nav.path[0][1] - q.y) < 0.35) nav.path.shift();
  const wp = nav.path[0] ?? [gx, gy];
  const r = walkTo(game, q, wp[0], wp[1], dt, sprint);
  if (r < 0) nav.stuck += dt; else nav.stuck = 0;
  return Math.hypot(gx - q.x, gy - q.y) * (r < 0 ? -1 : 1);
}

function houseOf(game: Game, prop: Property) { const [bx, by] = prop.key.split(',').map(Number); return game.world.buildingAt(bx + 0.5, by + 0.5); }

export function stepThreats(game: Game, dt: number): void {
  stepGuards(game, dt);
  stepBurglars(game, dt);
  stepPolice(game, dt);
  stepResidents(game, dt);
  stepGangs(game, dt);
  stepExtortion(game);
}

// ---- people indoors: residents wander their home, shopkeepers stand at the counter ----

const populatedBy = new WeakMap<Game, Map<string, number>>(); // per game: building key → game time populated (respawn after the area was unloaded)
function populatedOf(game: Game): Map<string, number> { let m = populatedBy.get(game); if (!m) { m = new Map(); populatedBy.set(game, m); } return m; }

/** Called when a player walks into a building: spawn its people once (kit decides who). */
export function populate(game: Game, key: string): void {
  const [bx, by] = key.split(',').map(Number);
  const b = game.world.buildingAt(bx + 0.5, by + 0.5); if (!b) return;
  const plan = floorPlan(b, 0, game.seed); if (!plan) return;
  if (game.people.some((q) => q.resident?.key === key)) return;
  const populated = populatedOf(game);
  const last = populated.get(key);
  if (last !== undefined && game.time - last < 120) return; // recently cleared out (shot, fled): stays empty a while
  populated.set(key, game.time);
  const rng = game.npcRng;
  const cells: [number, number][] = [];
  for (let ly = 1; ly < plan.h - 1; ly++) for (let lx = 1; lx < plan.w - 1; lx++) if (plan.cells[ly * plan.w + lx] === CELL_FLOOR) cells.push([plan.x + lx, plan.y + ly]);
  if (!cells.length) return;
  const spawn = (role: 'resident' | 'keeper', at: [number, number]) => {
    const q = makePerson(at[0] + 0.5, at[1] + 0.5);
    q.inside = key; q.floor = 0; q.role = role;
    q.resident = { key, idle: role === 'keeper' ? 1e9 : rng.float() * 4, flee: 0 };
    game.people.push(q);
    return q;
  };
  if (plan.kit === 'shop') {
    // the keeper stands against the wall opposite the door, in the middle
    const back = cells.slice().sort((a, c) => {
      const da = b.rot === 0 ? a[1] : b.rot === 2 ? -a[1] : b.rot === 1 ? -a[0] : a[0];
      const dc = b.rot === 0 ? c[1] : b.rot === 2 ? -c[1] : b.rot === 1 ? -c[0] : c[0];
      return da - dc || Math.abs(a[0] - (b.x + b.w / 2)) + Math.abs(a[1] - (b.y + b.h / 2)) - (Math.abs(c[0] - (b.x + b.w / 2)) + Math.abs(c[1] - (b.y + b.h / 2)));
    })[0];
    spawn('keeper', back);
    if (rng.chance(0.4)) spawn('resident', rng.pick(cells)); // a customer
  } else if (plan.kit === 'house' || plan.kit === 'apartment') {
    const n = Math.min(3, 1 + Math.floor((b.w * b.h) / 45));
    const prop = game.props.get(key);
    if (prop) return; // an owned house has no residents (the owner lives there)
    for (let i = 0; i < n; i++) if (rng.chance(0.7)) spawn('resident', rng.pick(cells));
  } else if (plan.kit === 'office') {
    const n = Math.min(4, 1 + Math.floor((b.w * b.h) / 60));
    for (let i = 0; i < n; i++) if (rng.chance(0.6)) spawn('resident', rng.pick(cells));
  }
}

function stepResidents(game: Game, dt: number): void {
  for (let i = game.people.length - 1; i >= 0; i--) {
    const q = game.people[i]; const ai = q.resident; if (!ai) continue;
    if (q.dead) { q.deadFor += dt; continue; }
    if (ai.flee > 0) {
      // out of the house and away
      ai.flee -= dt;
      const [bx, by] = ai.key.split(',').map(Number);
      const b = game.world.buildingAt(bx + 0.5, by + 0.5);
      if (b) {
        const door = doorstep(b, game.seed);
        const plan = game.world.planAt(b.x + 0.5, b.y + 0.5, 0); const dt0 = plan?.doors[0];
        const goal = q.inside ? (dt0 ? { x: dt0[0] + 0.5 + (b.rot === 1 ? -1 : b.rot === 3 ? 1 : 0), y: dt0[1] + 0.5 + (b.rot === 0 ? 1 : b.rot === 2 ? -1 : 0) } : door) : { x: door.x + (b.rot === 1 ? -12 : b.rot === 3 ? 12 : 0), y: door.y + (b.rot === 0 ? 12 : b.rot === 2 ? -12 : 0) };
        navTo(game, q, goal.x, goal.y, dt, true);
        if (!q.inside && Math.hypot(q.x - door.x, q.y - door.y) > 6) ai.flee = Math.min(ai.flee, 0.5); // clear of the house: calm down soon
        if (!q.inside && ai.flee <= 0) { q.resident = undefined; q.role = undefined; q.walker = { dx: 0, dy: 0, think: 0, idle: 0 }; } // becomes a pedestrian
      } else game.people.splice(i, 1);
      continue;
    }
    if (q.role === 'keeper') continue; // stands still
    ai.idle -= dt;
    if (ai.idle > 0) continue;
    if (!ai.goal) {
      const plan = game.world.planAt(q.x, q.y, q.floor);
      if (!plan) { ai.goal = undefined; ai.idle = 5; continue; }
      const cells: [number, number][] = [];
      for (let ly = 1; ly < plan.h - 1; ly++) for (let lx = 1; lx < plan.w - 1; lx++) if (plan.cells[ly * plan.w + lx] === CELL_FLOOR) cells.push([plan.x + lx, plan.y + ly]);
      const c = game.npcRng.pick(cells); if (!c) { ai.idle = 5; continue; }
      ai.goal = [c[0] + 0.5, c[1] + 0.5]; ai.idle = 0; q.resident!.flee = 0;
      (ai as { t?: number }).t = 0;
    }
    const r = navTo(game, q, ai.goal[0], ai.goal[1], dt);
    const t = ((ai as { t?: number }).t = ((ai as { t?: number }).t ?? 0) + dt);
    if (Math.abs(r) < 0.3 || r < 0 && t > 3 || t > 12) { ai.goal = undefined; ai.idle = 2 + game.npcRng.float() * 6; }
  }
}

/** Gunfire / explosions indoors: residents run for the door. */
export function scareResidents(game: Game, x: number, y: number, radius: number): void {
  for (const q of game.people) if (q.resident && !q.dead && Math.hypot(q.x - x, q.y - y) < radius) { q.resident.flee = 12; q.resident.goal = undefined; populatedOf(game).set(q.resident.key, game.time); }
}

// ---- police on foot (raids) ----

/** Two officers get out of a stopped patrol car and go after the wanted player, indoors too. */
export function spawnOfficers(game: Game, x: number, y: number, targetId: number, n = 2): void {
  const gun = game.reg.get('weapon', 'pistol_9mm') ?? game.reg.all('weapon').find((w) => w.class === 'pistol');
  for (let i = 0; i < n; i++) {
    const q = makePerson(x + (i ? 1 : -1) * 1.2, y + 0.8, false);
    q.role = 'police'; q.hp = q.maxHp = 80;
    q.police = { target: targetId, cool: 1 + i * 0.5, t: 0 };
    if (gun) q.weapons = [{ def: { ...gun, damage: 6 }, mag: gun.magazine, reserve: -1 }];
    game.people.push(q);
  }
}

/** Straight line free of walls at head height (interior walls count, windows and doors do not). */
function sightClear(game: Game, x0: number, y0: number, x1: number, y1: number): boolean {
  if (smokeBetween(game, x0, y0, x1, y1)) return false; // (Sep 30) nobody sees through a smoke grenade's cloud
  const n = Math.ceil(Math.hypot(x1 - x0, y1 - y0) * 2);
  for (let i = 1; i < n; i++) { const t = i / n; if (game.world.solidAt(x0 + (x1 - x0) * t, y0 + (y1 - y0) * t, 1.2)) return false; }
  return true;
}

function stepPolice(game: Game, dt: number): void {
  for (let i = game.people.length - 1; i >= 0; i--) {
    const q = game.people[i]; const ai = q.police; if (!ai) continue;
    if (q.dead) { q.deadFor += dt; continue; }
    ai.t += dt; ai.cool = Math.max(0, ai.cool - dt);
    const st = game.players.get(ai.target);
    const tp = st?.person;
    // no target, or the heat is gone: walk off (vanish once nobody needs them)
    const gang = q.role === 'gang' || q.role === 'guard';
    if (gang) {
      // a gang member gives up when the target is dead, gone, or far away for a while — and goes back to the corner
      if (!st || !tp || tp.dead || Math.hypot(tp.x - q.x, tp.y - q.y) > 90 || ai.t > 120) { q.police = undefined; q.walker = { dx: 0, dy: 0, think: 1e9, idle: 1e9 }; continue; }
    }
    const bounty = q.role === 'bounty' || gang;
    if (!st || !tp || tp.dead || (!bounty && (st.wanted === 0 || ai.t > 180))) {
      if (ai.beat && st && tp && !tp.dead && (st.wanted === 0 || ai.t > 180) && ai.t > 3) { q.police = undefined; q.walker = { dx: 0, dy: 0, think: 0, idle: 1 }; continue; } // back on the beat
      if (bounty ? !st : (ai.t > 6 || !st || st.wanted === 0)) game.people.splice(i, 1); continue;
    }
    const dist = Math.hypot(tp.x - q.x, tp.y - q.y);
    if (dist > 60 && !bounty) { game.people.splice(i, 1); continue; } // outran them on foot: the cars take over
    if (bounty && dist > 40) { ai.cool = Math.max(ai.cool, 0.5); if (dist > 70) continue; } // a bounty target lurks rather than chases across town
    const b = tp.inside ? game.world.buildingAt(tp.x, tp.y) : undefined;
    const clear = dist < 14 && sightClear(game, q.x, q.y, tp.x, tp.y) && (q.inside === tp.inside || !tp.inside);
    if (clear) {
      q.angle = Math.atan2(tp.x - q.x, -(tp.y - q.y));
      const w = q.weapons[0];
      // an officer at one star does not shoot: he runs you down and cuffs you (busted — a fine, and you keep your skin)
      const arresting = q.role === 'police' && st.wanted <= 1 && !tp.vehicle;
      if (q.role === 'police' && !tp.vehicle && dist < 1.4) { ai.cuff = (ai.cuff ?? 0) + dt; if (ai.cuff > 0.8) { game.bust(st, q); continue; } } else ai.cuff = 0;
      const melee = (!!w && w.def.class === 'melee') || arresting;
      const reach = melee ? 1.3 : 5; // a knife or a bat has to get close; a gun keeps its distance
      if (w && !arresting && ai.cool <= 0 && (!melee || dist < reach + 0.4)) { game.fireWeapon(q.role === 'police' ? { ...w.def, damage: w.def.damage * game.police.dmg } : w.def, q.x + Math.sin(q.angle) * 0.6, q.y - Math.cos(q.angle) * 0.6, q.angle + (game.npcRng.float() - 0.5) * 0.2, game.personBase(q) + 1.2, -2); ai.cool = melee ? 0.6 : 0.8; if (melee) q.swing = 0.28; }
      if (dist > reach) walkTo(game, q, tp.x, tp.y, dt, melee);
      continue;
    }
    // no line: move — into the building through its door when the target is inside, else straight at them
    let goal: { x: number; y: number };
    if (b && q.inside !== tp.inside) {
      const door = doorstep(b, game.seed);
      const plan = game.world.planAt(b.x + 0.5, b.y + 0.5, 0); const dt0 = plan?.doors[0];
      const atStep = Math.hypot(q.x - door.x, q.y - door.y) < 0.5 || !!ai.goal;
      if (atStep && dt0) { ai.goal = [dt0[0] + 0.5 + (b.rot === 1 ? 1 : b.rot === 3 ? -1 : 0), dt0[1] + 0.5 + (b.rot === 0 ? -1 : b.rot === 2 ? 1 : 0)]; goal = { x: ai.goal[0], y: ai.goal[1] }; }
      else goal = door;
    } else { ai.goal = undefined; goal = { x: tp.x, y: tp.y }; }
    navTo(game, q, goal.x, goal.y, dt, dist > 10);
    // watchdog: stuck for 5 s (a wall between rooms) — nudge sideways
    const dGoal = Math.hypot(q.x - goal.x, q.y - goal.y);
    if (ai.bestD === undefined || dGoal < ai.bestD - 0.3) { ai.bestD = dGoal; ai.bestAt = ai.t; }
    if (ai.t - (ai.bestAt ?? ai.t) > 5) { const a = game.npcRng.float() * Math.PI * 2; walkTo(game, q, q.x + Math.cos(a) * 3, q.y + Math.sin(a) * 3, dt); if (ai.t - (ai.bestAt ?? ai.t) > 7) { ai.bestD = undefined; } }
  }
}

// ---- guards ----

function stepGuards(game: Game, dt: number): void {
  for (const prop of game.props.values()) {
    const guards = prop.defences.filter((d) => game.reg.get('defence', d.id)?.kind === 'guard');
    if (!guards.length) continue;
    const b = houseOf(game, prop); if (!b) continue; // chunk not live: guards sleep
    const door = doorstep(b, game.seed);
    const near = [...game.players.values()].some((st) => Math.hypot(st.person.x - door.x, st.person.y - door.y) < 140);
    guards.forEach((d, i) => {
      let g = game.people.find((q) => q.guard?.key === prop.key && q.guard.post[0] === i);
      if (!g) {
        const def = game.reg.get('defence', d.id)!;
        const ownerNow = guardFollows(game.reg, d) ? game.ownerOnline(prop) : undefined;
        if (!near && !ownerNow) return; // nobody around: no need to simulate
        // (re)spawn at the door, one tile apart per guard (a bodyguard at the owner's side); a guard who was killed is gone from the book already
        g = makePerson(ownerNow ? ownerNow.person.x + 1.2 : door.x + (i % 2 ? 1.2 : -1.2) * (i > 1 ? 1.8 : 1), ownerNow ? ownerNow.person.y : door.y, false);
        if (ownerNow) { g.inside = ownerNow.person.inside; g.floor = ownerNow.person.floor; }
        g.hp = g.maxHp = d.hp > 0 ? d.hp : def.hp ?? 100;
        g.guard = { key: prop.key, cool: 0, post: [i, 0] }; g.role = 'guard';
        const w = def.weapon ? game.reg.get('weapon', def.weapon) : undefined;
        if (w) g.weapons = [{ def: w, mag: w.magazine, reserve: -1 }];
        game.people.push(g);
      }
      if (g.dead) { prop.defences.splice(prop.defences.indexOf(d), 1); const o = game.ownerOnline(prop); if (o) game.say(`Your guard at the ${prop.name.toLowerCase()} was killed`, o.person); return; }
      d.hp = g.hp; // the book remembers wounds
      const def = game.reg.get('defence', d.id)!;
      if (!powered(prop)) {
        // unpaid: the guard walks off the book — and if the owner is around, comes for what he is owed
        const o = game.ownerOnline(prop);
        prop.defences.splice(prop.defences.indexOf(d), 1);
        g.guard = undefined;
        if (o && Math.hypot(o.person.x - g.x, o.person.y - g.y) < 80) { g.police = { target: o.person.id, cool: 1, t: 0 }; game.say(`Your unpaid ${def.name.toLowerCase()} at the ${prop.name.toLowerCase()} has had enough — he is coming for you`, o.person); }
        else { g.walker = { dx: 0, dy: 0, think: 0, idle: 1 }; g.role = undefined; if (o) game.say(`Your unpaid ${def.name.toLowerCase()} walked off the ${prop.name.toLowerCase()}`, o.person); }
        return;
      }
      const ai = g.guard!;
      ai.cool = Math.max(0, ai.cool - dt);
      const range = def.range ?? 16;
      // target: the nearest remembered threat outside, or a stranger at the door
      let target: Person | undefined, bd = Infinity;
      for (const q of game.people) {
        if (q === g || q.dead || q.vehicle || q.guard) continue;
        const dist = Math.hypot(q.x - g.x, q.y - g.y); if (dist > range || dist >= bd) continue;
        const hostile = game.isThreat(prop, q) || (q.burglar?.key === prop.key) || (q.inside === prop.key && game.stranger(prop, q));
        if (!hostile) continue;
        target = q; bd = dist;
      }
      // a bodyguard walks with the owner instead of holding the door (rides along in the car)
      const owner = game.ownerOnline(prop);
      const body = guardFollows(game.reg, d);
      if (body) {
        if (!owner) { g.guard!.cool = 1; return; }
        const op = owner.person;
        if (op.vehicle && op.vehicle.driver === op && g.vehicle !== op.vehicle && op.vehicle.passengers.length + 1 < op.vehicle.def.seats && Math.hypot(op.x - g.x, op.y - g.y) < 4) { g.vehicle = op.vehicle; op.vehicle.passengers.push(g); }
        if (g.vehicle && (!op.vehicle || op.vehicle !== g.vehicle)) { g.vehicle.passengers = g.vehicle.passengers.filter((q) => q !== g); g.vehicle = undefined; g.x = op.x + 1; g.y = op.y; }
        if (g.vehicle) { g.x = g.vehicle.x; g.y = g.vehicle.y; return; }
        if (Math.hypot(op.x - g.x, op.y - g.y) > 60) { g.x = op.x + 1.5; g.y = op.y; g.inside = op.inside; g.floor = op.floor; } // lost: catches up (respawned at your side)
        // shoot anyone who hurt the owner recently, anywhere
        let tgt: Person | undefined, td = Infinity;
        for (const q of game.people) { if (q === g || q.dead || q.vehicle || q.guard) continue; const dd = Math.hypot(q.x - g.x, q.y - g.y); if (dd > range || dd >= td) continue; if (game.isThreat(prop, q) || q.police?.target === op.id || q.role === 'bounty') { tgt = q; td = dd; } }
        if (tgt) { const weapon = g.weapons[0]; g.angle = Math.atan2(tgt.x - g.x, -(tgt.y - g.y)); if (weapon && ai.cool <= 0) { game.fireWeapon(weapon.def, g.x + Math.sin(g.angle) * 0.6, g.y - Math.cos(g.angle) * 0.6, g.angle + (game.npcRng.float() - 0.5) * 0.12, game.personBase(g) + 1.2, g.id, prop.owner); ai.cool = 1 / Math.min(weapon.def.fireRate, 5) + 0.1; } }
        const dd = Math.hypot(op.x - g.x, op.y - g.y);
        if (dd > 2.2) navTo(game, g, op.x, op.y, dt, dd > 8);
        return;
      }
      if (target && !target.inside) {
        const weapon = g.weapons[0];
        g.angle = Math.atan2(target.x - g.x, -(target.y - g.y));
        if (weapon && ai.cool <= 0) {
          game.fireWeapon(weapon.def, g.x + Math.sin(g.angle) * 0.6, g.y - Math.cos(g.angle) * 0.6, g.angle + (game.npcRng.float() - 0.5) * 0.12, game.personBase(g) + 1.2, g.id, prop.owner);
          ai.cool = 1 / Math.min(weapon.def.fireRate, 4) + 0.15;
        }
        // close in a little, but never far from the post
        if (bd > 6 && Math.hypot(g.x - door.x, g.y - door.y) < GUARD_LEASH) walkTo(game, g, target.x, target.y, dt);
      } else {
        const post = { x: door.x + (i % 2 ? 1.2 : -1.2) * (i > 1 ? 1.8 : 1), y: door.y };
        if (Math.hypot(g.x - post.x, g.y - post.y) > 0.6) walkTo(game, g, post.x, post.y, dt);
        else g.angle = b.rot === 0 ? 0 : b.rot === 2 ? Math.PI : b.rot === 1 ? -Math.PI / 2 : Math.PI / 2; // face the street
      }
    });
  }
}

// ---- burglars ----

/** A burglar sets off for the house from the street (also used by tests). */
export function spawnBurglar(game: Game, prop: Property): Person | undefined {
  const b = houseOf(game, prop); if (!b) return undefined;
  const door = doorstep(b, game.seed);
  const owner = game.ownerOnline(prop);
  const q = makePerson(door.x + (b.rot === 1 ? -12 : b.rot === 3 ? 12 : (game.npcRng.float() - 0.5) * 16), door.y + (b.rot === 0 ? 12 : b.rot === 2 ? -12 : (game.npcRng.float() - 0.5) * 16));
  q.burglar = { key: prop.key, phase: 'approach', t: 0, goal: [door.x, door.y], stuck: 0 }; q.role = 'burglar';
  q.hp = q.maxHp = 60;
  game.people.push(q);
  if (owner && game.hasDefence(prop, 'camera')) game.say(`📷 Someone is creeping up on your ${prop.name.toLowerCase()}`, owner.person);
  return q;
}

function stepBurglars(game: Game, dt: number): void {
  // spawn: at night, per house, when the owner is not inside and someone is around to see it
  if (NIGHT(game.hour) && Math.floor(game.time * 10) % 50 === 0) { // ~ every 5 s, scaled to a per-hour chance
    for (const prop of game.props.values()) {
      if (game.people.some((q) => q.burglar?.key === prop.key)) continue;
      const b = houseOf(game, prop); if (!b) continue;
      const door = doorstep(b, game.seed);
      const owner = game.ownerOnline(prop);
      if (owner && owner.person.inside === prop.key) continue;
      const watcher = [...game.players.values()].some((st) => Math.hypot(st.person.x - door.x, st.person.y - door.y) < 150);
      if (!watcher) continue;
      const perTick = BURGLAR_CHANCE_PER_HOUR * (5 * game.daySpeed); // 5 s of game time in hours
      if (!game.npcRng.chance(perTick)) continue;
      spawnBurglar(game, prop);
    }
  }
  for (let i = game.people.length - 1; i >= 0; i--) {
    const q = game.people[i]; const ai = q.burglar; if (!ai) continue;
    if (q.dead) { q.deadFor += dt; continue; } // the loot they carried dropped with them (dropLoot)
    const prop = game.props.get(ai.key);
    const b = prop && houseOf(game, prop);
    if (!prop || !b) { game.people.splice(i, 1); continue; }
    const door = doorstep(b, game.seed);
    const plan = game.world.planAt(b.x + 0.5, b.y + 0.5, 0);
    const doorTile = plan?.doors[0];
    ai.t += dt;
    if (ai.phase === 'approach') {
      // to the doorstep first (in line with the door), then straight through it
      const locked = game.hasDefence(prop, 'door');
      if (!q.inside) {
        if (Math.hypot(q.x - door.x, q.y - door.y) < 0.4 && doorTile && !locked) ai.goal = [doorTile[0] + 0.5 + (b.rot === 1 ? 1 : b.rot === 3 ? -1 : 0), doorTile[1] + 0.5 + (b.rot === 0 ? -1 : b.rot === 2 ? 1 : 0)];
        const r = walkTo(game, q, ai.goal[0], ai.goal[1], dt);
        if (r < 0) ai.stuck += dt;
      }
      if (q.inside === prop.key) { ai.phase = 'inside'; ai.t = 0; game.threat(prop, q); const o = game.ownerOnline(prop); if (o && game.hasDefence(prop, 'camera')) game.say(`📷 A burglar is inside your ${prop.name.toLowerCase()}!`, o.person); }
      else {
        if (locked && Math.hypot(q.x - door.x, q.y - door.y) < 1.2 && ai.t < 36) ai.t = 36; // reinforced: rattles the handle for a few seconds, then gives up
        if (ai.t > 40 || ai.stuck > 6) { ai.phase = 'leave'; ai.t = 0; }
      }
    } else if (ai.phase === 'inside') {
      // rummage (turn around, a step or two deeper in), then bag the loot
      if (q.inside !== prop.key) walkTo(game, q, ai.goal[0], ai.goal[1], dt);
      else if (Math.floor(ai.t) % 3 === 0) q.angle += dt * 2;
      if (ai.t >= BURGLAR_STAY) {
        const cash = Math.floor(prop.cash * 0.25); prop.cash -= cash; if (cash > 0) q.items.cash = (q.items.cash ?? 0) + cash;
        const ids = Object.keys(prop.stash.items).filter((id) => prop.stash.items[id] > 0);
        const took: string[] = [];
        if (ids.length) { const id = ids[Math.floor(game.npcRng.float() * ids.length)]; prop.stash.items[id]--; if (prop.stash.items[id] <= 0) delete prop.stash.items[id]; q.items[id] = (q.items[id] ?? 0) + 1; took.push(game.reg.get('item', id)?.name ?? id); }
        if (cash > 0) took.unshift(`$${cash}`);
        const o = game.ownerOnline(prop);
        if (!o && took.length) (prop.log ??= []).push(`burgled on day ${game.day}: ${took.join(', ')} gone`); // (told on the owner's next join)
        if (o) game.say(took.length ? `Burglary at your ${prop.name.toLowerCase()}: ${took.join(', ')} gone (the thief is leaving — get them)` : `A burglar went through your ${prop.name.toLowerCase()} — nothing worth taking`, o.person);
        ai.phase = 'leave'; ai.t = 0;
      }
    } else {
      // out through the door, then away down the street
      const away = { x: door.x + (b.rot === 1 ? -25 : b.rot === 3 ? 25 : 0), y: door.y + (b.rot === 0 ? 25 : b.rot === 2 ? -25 : 0) };
      const goal = q.inside ? door : away; // the doorstep is outside: the route goes out through the door tile
      navTo(game, q, goal.x, goal.y, dt, true);
      // watchdog: no progress towards the goal for 3 s = stuck on a tree or a fence, vanish down the street
      const dGoal = Math.hypot(q.x - goal.x, q.y - goal.y);
      if (ai.bestD === undefined || dGoal < ai.bestD - 0.5) { ai.bestD = dGoal; ai.bestAt = ai.t; }
      const stuck = ai.t - (ai.bestAt ?? ai.t) > 3;
      if (!q.inside && (dGoal < 2 || ai.t > 45 || stuck)) game.people.splice(i, 1);
    }
  }
}

// ---- gangs: groups that hang out at a doorstep and come for whoever provokes them ----

/** Turn the whole group (same gang, within 30 tiles of the one hurt) on a player. */
export function provokeGang(game: Game, hurt: Person, by: Person): void {
  if (!hurt.gang || !by.isPlayer) return;
  let n = 0;
  for (const q of game.people) {
    if (q.dead || q.role !== 'gang' || q.gang?.id !== hurt.gang.id || Math.hypot(q.x - hurt.x, q.y - hurt.y) > 30) continue;
    if (q.police?.target === by.id) continue;
    q.walker = undefined; q.police = { target: by.id, cool: 0.3 + game.npcRng.float() * 0.6, t: 0 }; n++;
  }
  if (n) game.say(`${game.reg.get('gang', hurt.gang.id)?.name ?? 'The gang'} — they're coming for you`, by);
}

function stepGangs(game: Game, dt: number): void {
  for (const q of game.people) {
    const g = q.gang; if (!g || q.dead || q.police) continue;
    // idle: shuffle around the doorstep, face the street; a member that got separated walks back
    const dh = Math.hypot(q.x - g.home[0], q.y - g.home[1]);
    if (dh > 4) { walkTo(game, q, g.home[0] + (game.npcRng.float() - 0.5) * 2, g.home[1] + (game.npcRng.float() - 0.5) * 2, dt); }
    else { g.idle -= dt; if (g.idle <= 0) { g.idle = 2 + game.npcRng.float() * 5; q.angle = game.npcRng.float() * Math.PI * 2; } }
    // provocation: a gun out near an 'armed' gang, anyone near an 'always' gang
    const def = game.reg.get('gang', g.id); if (!def) continue;
    for (const st of game.players.values()) {
      const p = st.person; if (p.dead || p.vehicle || p.inside !== q.inside) continue;
      const grudge = (st.stats[`rep_${g.id}`] ?? 0) <= -3; // killed three of theirs: they attack on sight
      if (def.hostility === 'provoked' && !grudge) continue;
      const d = Math.hypot(p.x - q.x, p.y - q.y);
      if (d > (def.hostility === 'always' || grudge ? 10 : 8)) continue;
      const armed = p.weapons[p.weaponIndex] && p.weapons[p.weaponIndex].def.class !== 'melee';
      if (def.hostility === 'always' || grudge || armed) { provokeGang(game, q, p); break; }
    }
  }
}

// ---- extortion: the crew leaning on a player's business stands at its door until they are dealt with ----
function stepExtortion(game: Game): void {
  for (const prop of game.props.values()) {
    const ex = prop.extort; if (!ex) continue;
    const [bx, by] = prop.key.split(',').map(Number);
    const b = game.world.buildingAt(bx + 0.5, by + 0.5); if (!b) continue;
    const d = doorstep(b, game.seed);
    const crew = game.people.filter((q) => q.gang?.id === ex.gang && Math.hypot(q.gang.home[0] - d.x, q.gang.home[1] - d.y) < 1);
    const near = game.nearestPlayerDist(d.x, d.y);
    if (!ex.spawned) {
      if (near < 70) { const made = spawnGangAt(game, { x: d.x, y: d.y, name: prop.name, key: prop.key }, ex.gang); if (made.length) ex.spawned = true; }
      continue;
    }
    if (crew.length && crew.every((q) => q.dead)) {
      prop.extort = undefined;
      const owner = game.ownerOnline(prop); if (owner) karma(game, owner, KARMA.extortionCleared, 'you ran a crew off your business'); if (owner) game.say(`You ran the ${game.reg.get('gang', ex.gang)?.name ?? 'crew'} off ${prop.name} — the till is yours again`, owner.person);
    } else if (!crew.length && near > 160) ex.spawned = false; // they wandered off / despawned: back next time you come by
  }
}
